import React from 'react';
import { Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';

import TextField from './fields/TextField';
import NumberField from './fields/NumberField';
import DateField from './fields/DateField';
import DropdownField from './fields/DropdownField';
import CheckboxField from './fields/CheckboxField';
import CheckboxGroupField from './fields/CheckboxGroupField';
import FileField from './fields/FileField';
import SignatureField from './fields/SignatureField';
import ProfileField from './fields/ProfileField';
import EmailField from './fields/EmailField';
import PhoneField from './fields/PhoneField';
import AddressField from './fields/AddressField';
import NameField from './fields/NameField';
import PaymentField from './fields/PaymentField';

const FIELD_COMPONENTS = {
    text: TextField,
    longtext: TextField,
    number: NumberField,
    date: DateField,
    datetime: DateField,
    time: DateField,
    dropdown: DropdownField,
    checkbox: CheckboxField,
    checkboxGroup: CheckboxGroupField,
    file: FileField,
    signature: SignatureField,
    profile: ProfileField,
    email: EmailField,
    phone: PhoneField,
    address: AddressField,
    name: NameField,
    payment: PaymentField,
};

/**
 * Switches on `field.type` and renders the matching field component. Wires the
 * label, required indicator, description tooltip, placeholder, and default
 * value through to each field component (which delegates to the reused General
 * inputs that already render label/required/help-text). Handles `hidden` fields
 * (renders nothing) and renders the error message slot (red text) below the
 * field.
 *
 * Props:
 * - field: schema field object ({ id, fieldId, type, label, description, placeholder, defaultValue, hidden, required, config })
 * - value: current payload value for this field
 * - onChange(newValue): callback when the value changes
 * - error: string | null — validation error to display below the field
 * - disabled: bool — read-only/preview mode (legacy; prefer renderMode)
 * - renderMode: 'fill' | 'preview' | 'submission' — explicit rendering intent.
 *   `disabled` is derived: preview + submission always render read-only.
 * - formContext: { formID, formIsPublic, contentRevision, onSignaturePending }
 *   shared context for fields that need form-level behaviour (signature
 *   pending tracking, public-form gating).
 * - loggedInUser: optional person object (NameField autofill, ProfileField
 *   directory permission)
 * - paymentContext: { payment, onPaymentChange, loggedInUser }
 */
const FieldRenderer = ({ field, value, onChange, error, disabled, renderMode = 'fill', formContext, loggedInUser, paymentContext }) => {
    if(field.hidden) return null;

    const Component = FIELD_COMPONENTS[field.type];
    const isDisabled = disabled || renderMode !== 'fill';

    if(!Component){
        return (
            <View style={styles.container}>
                <Text style={styles.unsupported}>
                    Unsupported field type: {field.type}
                </Text>
            </View>
        );
    }

    return (
        <View style={styles.container}>
            <Component
                field={field}
                value={value}
                onChange={onChange}
                disabled={isDisabled}
                renderMode={renderMode}
                formContext={formContext}
                loggedInUser={loggedInUser}
                paymentContext={paymentContext}
            />
            {!!error && <Text style={styles.error}>{error}</Text>}
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts, spacing, typography }) => ({
    container: {
        marginVertical: spacing.sm,
        width: '100%',
    },
    error: {
        color: colors.error,
        fontSize: typography.caption,
        fontFamily: fonts.body,
        paddingLeft: 4,
        paddingTop: 2,
    },
    unsupported: {
        color: colors.warning,
        fontSize: typography.caption,
        fontFamily: fonts.body,
        fontStyle: 'italic',
    },
}));

export default FieldRenderer;
